import * as Api from '@ViewModels';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { Category, CreateCategoryInput, UpdateCategoryInput } from '../../voice-agent/types/scenarios';
import { extractArrayFromResponse } from '../utils';

const CATEGORIES_QUERY_KEY = 'categories';

/**
 * Fetch all categories
 */
export function useCategories({ enabled = true }: { enabled?: boolean } = {}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<
				Category[] | { value?: Category[]; values?: Category[] }
			>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/categories' }), 'GET');
			return extractArrayFromResponse<Category>(response.value);
		},
		queryKey: [CATEGORIES_QUERY_KEY],
	});
}

/**
 * Fetch categories for the current user's role (determined server-side, defaults to SDR)
 * Use this for user-facing views like training dashboard and scenario selection.
 */
export function useMyCategories({ enabled = true }: { enabled?: boolean } = {}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<
				Category[] | { value?: Category[]; values?: Category[] }
			>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/categories/categories' }), 'GET');
			return extractArrayFromResponse<Category>(response.value);
		},
		queryKey: [CATEGORIES_QUERY_KEY, 'categories'],
	});
}

/**
 * Fetch categories filtered by role
 * Use for admin use cases where you need data for a specific role regardless of current user.
 */
export function useCategoriesByRole({ role, enabled = true }: { role: string; enabled?: boolean }) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!role,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<
				Category[] | { value?: Category[]; values?: Category[] }
			>(Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/categories/role/${encodeURIComponent(role)}` }), 'GET');
			return extractArrayFromResponse<Category>(response.value);
		},
		queryKey: [CATEGORIES_QUERY_KEY, 'role', role],
	});
}

/**
 * Fetch a single category by ID
 */
export function useCategory({ id, enabled = true }: { id: string; enabled?: boolean }) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!id,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<Category>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/categories/${id}` }),
				'GET'
			);
			return response.value;
		},
		queryKey: [CATEGORIES_QUERY_KEY, id],
	});
}

/**
 * Create a new category
 */
export function useCreateCategoryMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateCategoryInput): Promise<Category> => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<Category>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/categories' }),
				'POST',
				input
			);

			if (!response.value) {
				throw new Error('No data returned from create category API');
			}

			return response.value;
		},
		onError: (error: Error) => {
			console.error('Failed to create category:', error);
		},
		onSuccess: () => {
			// Invalidate all category queries to refresh data
			queryClient.invalidateQueries({ queryKey: [CATEGORIES_QUERY_KEY] });
		},
	});
}

/**
 * Update an existing category
 */
export function useUpdateCategoryMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, input }: { id: string; input: UpdateCategoryInput }): Promise<Category> => {
			if (!id) {
				throw new Error('Category ID is required');
			}

			const response = await userSession.webServiceHelper.callWebServiceAsync<Category>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/categories/${id}` }),
				'PUT',
				input
			);

			if (!response.value) {
				throw new Error('No data returned from update category API');
			}

			return response.value;
		},
		onError: (error: Error) => {
			console.error('Failed to update category:', error);
		},
		onSuccess: () => {
			// Invalidate all category queries to refresh data
			queryClient.invalidateQueries({ queryKey: [CATEGORIES_QUERY_KEY] });
		},
	});
}

/**
 * Delete a category
 */
export function useDeleteCategoryMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<void> => {
			if (!id) {
				throw new Error('Category ID is required');
			}

			await userSession.webServiceHelper.callWebServiceAsync(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/categories/${id}` }),
				'DELETE'
			);
		},
		onError: (error: Error) => {
			console.error('Failed to delete category:', error);
		},
		onSuccess: () => {
			// Invalidate all category queries to refresh data
			queryClient.invalidateQueries({ queryKey: [CATEGORIES_QUERY_KEY] });
		},
	});
}
